import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";

@Component({
  selector: "app-table",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, AfterViewInit {
  // @Input() selectableRows = false;
  @Input() draggable :boolean = false //can adjust the columns width or the rows height
  @Input() minWidth = "100%";
  @Input() clickableRows = false;
  @Input() searchByOptions = [];
  @Input() searchBy = "";
  @Input() maxHeight = "60vh"; //fixed height is needed for both sticky headers and for virtual scroll
  @Input() filterInput = "";
  @Input() hasBorders = false;
  @Input() hasAlternatingRows = false;
  @Input() createText = "";
  @Input() isLoading = false;
  @Input() isProcessing = false;
  @Input() actions: TableAction[] = [];
  @Input() showRowCountColumn = false;
  @Input() tableLayout = "fixed";
  @Input() sortBy;
  @Input() rows = [];
  @Input() columns: TableColumn[] = [];
  @Input() showPaginator = false;
  @Input() pageNumber = 0;
  @Input() perPage = 10;
  @Input() perPageOptions = [{ count: 10 }, { count: 25 }, { count: 50 }];
  @Input() itemCount = 0;
  @Input() showCreate = true;
  @Input() canSearch = false;
  @Input() undefinedText = "Undefined";
  @Input() sortAsc;
  @Input() emptyText;
  @Input() rowMaxHeight = "none"; // if using virutal scroll, will default to 42px 
  @Input() useVirutalScroll: boolean = false;
  @Input() activatedRoute;
  @Input() canLoadMore = false; // allow to display a button to load more at the bottom of the page
  @Input() disableLoadMore = false; //disable state for load more button
  @Input() customCountLabel:string // to be used rather than the current item count/pagination count
  @Output() sortChange = new EventEmitter<any>();
  @Output() activePageChange = new EventEmitter<any>();
  @Output() actionClicked = new EventEmitter<any>();
  @Output() createClicked = new EventEmitter<any>();
  @Output() initSearch = new EventEmitter<any>();
  @Output() setPageCount = new EventEmitter<any>();
  @Output() updateSearchBy = new EventEmitter<any>();
  @Output() clickedRow = new EventEmitter<any>();
  @Output() clickedHeader = new EventEmitter<any>();
  @Output() checkedRow = new EventEmitter<any>();
  @Output() scaffoldLinkClicked = new EventEmitter<any>();
  @Output() rowInputUpdate = new EventEmitter<any>();
  @Output() columnRowClicked = new EventEmitter<any>();
  @Output() checkedColumn = new EventEmitter<TableColumn>();
  @Output() loadMore = new EventEmitter<void>();// output event when load more is clicked
  // public actionHeaderWidth = "0px";
  @ViewChild("table") table: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  public viewPort: CdkVirtualScrollViewport;
  public headerTop = "0px";
  public rowMaxWidth = "inherit";
  public tableWidth = "100%";
  public hasSetHeaders: boolean;
  public get inverseOfTranslation(): string {
    if (!this.viewPort || !this.viewPort["_renderedContentOffset"]) {
      return "-1px";
    }
    let offset = this.viewPort["_renderedContentOffset"];
    return `-${offset + 3}px`;
  }

  private startHeight: number;
  private startY: number;
  private startX: number;
  private startWidth: number;
  private currentTh: HTMLElement;
  private currentRow: HTMLElement;
  public isResizing: boolean = false;
  private isViewInitialized: boolean;
  private cd: ChangeDetectorRef;
  ngOnInit(): void {
    // if table tableout is not fixed, table headers can be considered set
    if (this.tableLayout !== "fixed") {
      this.hasSetHeaders = true;
    }
    // must enforce expected rowMaxHeight if using a virtual viewport to match expected item size
    if (this.useVirutalScroll && this.rowMaxHeight !== "44px") {
      this.rowMaxHeight = "44px";
    }
  }

  ngOnChanges(changes) {
    // checks only for fixed tables to sort out the width if columns change
    if (this.tableLayout === "fixed") {
      if (changes?.columns) {
        
        // ensure table is 100% if fixed width
        this.tableWidth = "100%";

        if (this.isViewInitialized) {
          setTimeout(() => this.setHeaderBasedWidths(), 1);
        }
      }
      if (
        !this.isLoading &&
        this.rows.length > 0 &&
        !this.hasSetHeaders &&
        this.isViewInitialized
      ) {
        setTimeout(() => this.setHeaderBasedWidths(), 1);
      }
    }
  }

  ngAfterViewInit() {
    // needing to set the headers width based on the header content plus the sort icon to avoid scrunched table views
    this.setHeaderBasedWidths();
    this.isViewInitialized = true;
    if (!!this.viewPort) {
      // have headers position updated when scrolling with sticky headers and a virtual scrolling viewport
      this.viewPort["_scrollStrategy"].onRenderedOffsetChanged = () =>
        (this.headerTop = `-${this.viewPort.getOffsetToRenderedContentStart()}px`);
    }
  }

  setHeaderBasedWidths() {
    const tableElement = this?.table?.nativeElement;
    if (!tableElement) return;
    const headers = tableElement.querySelectorAll("th");
    const rows = tableElement.querySelectorAll("tr");
    // calculate available width
    const minColumnWidth = 100;
    const actionsWidth = this.actions.length * 40;

    // First pass: get natural widths
    const naturalWidths = Array.from(headers).map((header, index) => {
      if (index === headers.length - 1 && this.actions.length > 0) {
        return actionsWidth;
      }

      // Get header width
      const headerWidth = this.getContentWidth(header as HTMLElement);

      // Get content widths from first 8 rows
      const contentWidths = Array.from(rows)
        .slice(1, 9)
        .filter((c) => !!c)
        .map((row: any) => {
          const cell = row.cells[index];
          // Get the actual text content width
          const textContent = cell.textContent || "";
          const tempSpan = document.createElement("span");
          tempSpan.style.visibility = "hidden";
          tempSpan.style.position = "absolute";
          tempSpan.style.whiteSpace = "nowrap";
          tempSpan.textContent = textContent;
          document.body.appendChild(tempSpan);
          const width = tempSpan.offsetWidth;
          document.body.removeChild(tempSpan);
          return width + 10;
        });

      // Find the maximum width needed
      const maxContentWidth = Math.max(headerWidth, ...contentWidths);

      // Return the larger of max content width or minimum width
      return Math.max(maxContentWidth, minColumnWidth);
    });

    // Calculate average width
    const avgColumnWidth =
      naturalWidths.reduce((sum, width) => sum + width, 0) /
      naturalWidths.length;

    // Second pass: adjust widths
    headers.forEach((header, index) => {
      const headerElement = header as HTMLElement;
      if (
        !headerElement.style.width ||
        headerElement.style.width.includes("auto")
      ) {
        if (index === headers.length - 1 && this.actions.length > 0) {
          headerElement.style.width = `${actionsWidth}px`;
          return;
        }

        // Calculate how much wider this column's content is compared to average
        const contentRatio = naturalWidths[index] / avgColumnWidth;
        const scaleFactor =
          contentRatio > 1.1 ? Math.log2(contentRatio + 1) : 1;

        let finalWidth = Math.max(
          naturalWidths[index] * scaleFactor,
          minColumnWidth
        );
        // after everything avoid anything above 300px
        finalWidth = finalWidth > 300 ? 300 : finalWidth;

        headerElement.style.width = `${Math.floor(finalWidth)}px`;
      }
    });
    this.hasSetHeaders = true;
  }

  getContentWidth(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.visibility = "hidden";
    clone.style.position = "absolute";
    clone.style.width = "auto";
    clone.style.whiteSpace = "nowrap";
    document.body.appendChild(clone);
    const width = clone.offsetWidth;
    document.body.removeChild(clone);

    const isHeader = element.tagName.toLowerCase() === "th";
    const basePadding = isHeader ? 20 : 0;
    const sortArrowPadding = isHeader ? 25 : 0;

    return width + basePadding + sortArrowPadding;
  }

  onColumnMouseDown(event: MouseEvent) {
    // avoid sorting a column when currently resizing a column
    this.isResizing = true;

    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startX = event.pageX;
    this.currentTh = (event.target as HTMLElement).parentElement;
    this.startWidth = this.currentTh.offsetWidth;

    document.addEventListener("mousemove", this.onColumnMouseMove);
    document.addEventListener("mouseup", this.onColumnMouseUp);
  }

  onColumnMouseMove = (event: MouseEvent) => {
    if (this.currentTh) {
      const deltaX = event.pageX - this.startX;
      const newWidth = this.startWidth + deltaX;
      this.currentTh.style.width = newWidth > 100 ? `${newWidth}px` : "100px";
    }
  };

  onColumnMouseUp = () => {
    document.removeEventListener("mousemove", this.onColumnMouseMove);
    document.removeEventListener("mouseup", this.onColumnMouseUp);
    this.currentTh = null;
    setTimeout(() => (this.isResizing = false), 10);
  };

  onRowMouseDown(event: MouseEvent) {
    // avoid other actions when resizing a row
    this.isResizing = true;
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startY = event.pageY;
    this.currentRow = (event.target as HTMLElement).closest("tr");
    this.startHeight = this.currentRow.offsetHeight;

    document.addEventListener("mousemove", this.onRowMouseMove);
    document.addEventListener("mouseup", this.onRowMouseUp);
  }

  trackByAction(index: number, action: TableAction) {
    return action.type;
  }

  trackByColumn(index: number, column: TableColumn) {
    return column?.property;
  }

  onRowMouseMove = (event: MouseEvent) => {
    if (this.currentRow) {
      const newHeight = this.startHeight + (event.pageY - this.startY);
      this.currentRow.style.height = newHeight > 50 ? `${newHeight}px` : "50";
      const finalHeight = newHeight > 50 ? `${newHeight}px` : "50";
      this.currentRow.style.height = finalHeight;
      // Update each child .app-table-content-wrapper max-height
      const contentWrappers = this.currentRow.querySelectorAll(
        ".app-table-content-wrapper"
      );
      this.currentRow.style.height = finalHeight;
      contentWrappers.forEach((wrapper: HTMLElement) => {
        wrapper.style.maxHeight = finalHeight;
      });
    }
  };

  onRowMouseUp = () => {
    this.isResizing = false;
    setTimeout(() => (this.isResizing = false), 10);

    document.removeEventListener("mousemove", this.onRowMouseMove);
    document.removeEventListener("mouseup", this.onRowMouseUp);
    this.currentRow = null;
  };

  onRowColumnMouseDown(event: MouseEvent, column) {
    this.isResizing = true;
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startX = event.pageX;
    this.startY = event.pageY;

    // Determine if resizing a row or column
    this.currentRow = (event.target as HTMLElement).closest("tr");
    const tableElement = this.table.nativeElement;
    const headers = tableElement.querySelectorAll("th");
    this.currentTh = Array.from(headers).find((th) => {
      return (
        (th as HTMLElement).innerText.trim().toLowerCase() ===
        column?.text?.toLowerCase()
      );
    }) as HTMLElement;

    if (this.currentRow) {
      this.startHeight = this.currentRow.offsetHeight;
    }

    if (this.currentTh) {
      this.startWidth = this.currentTh.offsetWidth;
    }

    document.addEventListener("mousemove", this.onRowColumnMouseMove);
    document.addEventListener("mouseup", this.onRowColumnMouseUp);
  }

  onRowColumnMouseMove = (event: MouseEvent) => {
    const deltaX = event.pageX - this.startX;

    if (this.currentTh) {
      // Horizontal resizing
      const newWidth = this.startWidth + deltaX;
      this.currentTh.style.width = newWidth > 100 ? `${newWidth}px` : "100px";
    }
  };

  onRowColumnMouseUp = () => {
    document.removeEventListener("mousemove", this.onRowColumnMouseMove);
    document.removeEventListener("mouseup", this.onRowColumnMouseUp);
    this.currentTh = null;
    this.isResizing = false;
  };

  handleCheckedRow(row, column) {
    this.checkedRow.emit({ row, column });
  }

  handleClickedRow($event, row) {
    if (this.isResizing) return;
    // oddly is hitting here before the child element is emitting.. need to avoid here
    if (
      !$event?.target ||
      $event?.target.closest("app-input-toggle") ||
      $event?.target.closest("textarea") ||
      $event?.target.closest("input")
    )
      return;
    if (!this.clickableRows) return;
    console.log(row);
    this.clickedRow.emit({ $event, row });
  }

  handleUpdateSearchBy($event) {
    this.updateSearchBy.emit($event);
  }

  handleActivePageChange($event) {
    this.activePageChange.emit($event);
  }

  handleActionClicked($event, action, row) {
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }
    this.actionClicked.emit({ action, row });
  }

  handleColumnRowClicked($event, row, property) {
    if (this.isResizing) return;
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }
    this.columnRowClicked.emit({ row, property });
  }

  handleCreateClicked() {
    this.createClicked.emit();
  }
  handleInitSearch(clear) {
    if (clear) this.filterInput = "";
    this.initSearch.emit(this.filterInput);
  }
  handleSetPageCount($event) {
    console.log('SET PAGE COUNT',$event);
    this.setPageCount.emit($event?.value);
  }

  handleSortChange($event) {
    if (this.isResizing) return;
    if ($event.isSortable) {
      this.sortChange.emit($event);
    }
  }

  handleCheckedHeaderRow($event, column) {
    this.clickedHeader.emit({ $event, column });
  }

  handleScaffoldLink($event, row, column) {
    $event.preventDefault();
    $event.stopPropagation();
    this.scaffoldLinkClicked.emit({ $event, row, column });
  }
  handleRowInputUpdate($event, type, row, column) {
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }

    this.rowInputUpdate.emit({ type, row, column });
  }

  getCountLabel() {
    const start = this.pageNumber * this.perPage - this.perPage + 1;
    const end = Math.min(this.pageNumber * this.perPage, this.itemCount);
    return `${start} - ${end} of ${this.itemCount} Items`;
  }

  trackByFn(index, item) {
    return item?.id || index;
  }

  getURL(item) {
    if (!item) return "";
    let _rel_url = "";
    if (
      this.activatedRoute &&
      this.activatedRoute.parent &&
      this.activatedRoute.parent.component
    ) {
      this.activatedRoute.parent.url.subscribe((val) => {
        const parts = val.map((v) => {
          return v.path;
        });
        _rel_url = "/" + parts.join("/") + "/";
        _rel_url += item?.type + "/" + item?.id;
      });
    } else {
      _rel_url = "/";
      _rel_url += item?.type + "/" + item?.id;
    }
    return _rel_url;
  }

  handleCheckedColumn(column) {
    this.checkedColumn.emit(column);
  }

  handleLoadMore() {
    this.loadMore.emit();
  }
}

export interface TableAction {
  type: "delete" | "edit" | "duplicate" | "view" | "info";
  hide?: string;
  tooltip?: string;
  permission?: string;
}

export interface TableColumn {
  text: string;
  property: string;
  type:
    | "string"
    | "boolean"
    | "date"
    | "link"
    | "locallink"
    | "template"
    | "textIcon"
    | "checkbox";
  headerCheckboxValue?: boolean;
  textTransform?: string;
  width?: string;
  isSortable?: boolean;
  isSorted?: boolean;
  translationKey?: string;
  template?: TemplateRef<any>;
}
